import { PhotoIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { InfoWithLabel } from "@/core/components/info-with-label";
import { ServiceTitanJobLink } from "@/core/components/service-titan-job-link";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useGetActivityYardSignPhoto } from "@/activity-feed/query";
import { useBusiness } from "@/core/business/use-business";
import { ISpiff, PhotoTrigger } from "@/spiffs/spiffs.d";

const FailedToLoadImage = () => (
  <div className="flex h-48 w-full flex-col items-center justify-center gap-2 rounded-lg border border-stroke-tertiary bg-card">
    <PhotoIcon className="h-6 w-6" />
    <Typography variant="bodyW2" textColor="secondary" className="text-center">
      Could not load photo
    </Typography>
  </div>
);

interface YardSignPhotoTriggerDetailsProps {
  spiff: ISpiff;
}

export const YardSignPhotoTriggerDetails = ({
  spiff,
}: YardSignPhotoTriggerDetailsProps) => {
  const { businessId } = useBusiness();
  const photoTrigger = spiff.trigger as PhotoTrigger;
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const { data: photo, isLoading } = useGetActivityYardSignPhoto(
    businessId,
    photoTrigger.id,
    true,
  );

  if (isLoading) {
    return (
      <InfoWithLabel label="Photo">
        <Skeleton className="h-48 w-full rounded-lg" />
      </InfoWithLabel>
    );
  }

  if (!photo?.photo_url) {
    return (
      <InfoWithLabel label="Photo">
        <FailedToLoadImage />
      </InfoWithLabel>
    );
  }

  return (
    <Stack gap="sm" fullWidth>
      {photo.service_titan_job_id && (
        <InfoWithLabel label="Associated Job">
          <ServiceTitanJobLink jobId={photo.service_titan_job_id} />
        </InfoWithLabel>
      )}
      <InfoWithLabel label="Photo">
        {imageLoadFailed ? (
          <FailedToLoadImage />
        ) : (
          <img
            src={photo.photo_url}
            alt="Yard sign"
            className="w-full rounded-lg border border-stroke-tertiary"
            onError={() => {
              setImageLoadFailed(true);
            }}
          />
        )}
      </InfoWithLabel>
    </Stack>
  );
};
